import React, { createContext, useContext, useEffect, useRef, useState } from "react";
(window as any).global = window;
import * as Chime from 'amazon-chime-sdk-js';
import { Channel } from "types";
import { useNavigate } from "react-router-dom";
const VoiceContext = createContext<{
    voice: any;
    setVoice: React.Dispatch<React.SetStateAction<any>>;
    joinVoiceChannel: (channel: Channel, teamId: string) => Promise<void>;
    channel: any;
    setChannel: React.Dispatch<React.SetStateAction<any>>;
    leaveVoiceChannel: () => Promise<void>;
    voiceData: { isMuted: boolean; isDeafened: boolean };
    setVoiceData: React.Dispatch<React.SetStateAction<{ isMuted: boolean; isDeafened: boolean }>>;
    meetingSessionRef: React.MutableRefObject<any>;
    setVoiceSettings: (settings: any) => void;
    startScreenSharing: () => Promise<MediaStream | undefined>;
    isScreenSharing: boolean;
    ping: number;
}>({
    voice: {},
    setVoice: () => { },
    joinVoiceChannel: async () => { },
    channel: {},
    setChannel: () => { },
    leaveVoiceChannel: async () => { },
    voiceData: { isMuted: false, isDeafened: false },
    setVoiceData: () => { },
    meetingSessionRef: { current: null },
    setVoiceSettings: () => { },
    startScreenSharing: () => { return new Promise<MediaStream | undefined>(() => { }) },
    isScreenSharing: false,
    ping: 0,
});

export const VoiceProvider = ({ children }: { children: React.ReactNode }) => {
    const [voice, setVoice] = useState<any>({});
    const [channel, setChannel] = useState<any>({});
    const meetingSessionRef = useRef<any>(null);
    const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
    const [, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
    const [, setIsElectron] = useState(false);
    const [voiceData, setVoiceData] = useState<{ isMuted: boolean; isDeafened: boolean }>(
        localStorage.getItem("voiceData") ? JSON.parse(localStorage.getItem("voiceData") as string) : { isMuted: false, isDeafened: false }
    );
    const navigate = useNavigate();

    useEffect(() => {
        const checkElectron = async () => {
            const result = await (window as any)?.electron?.isElectron();
            setIsElectron(Boolean(result));
        };
        checkElectron();
    }, []);

    useEffect(() => {
        const getAudioDevices = async () => {
            const devices = await navigator.mediaDevices.enumerateDevices();
            setAudioInputs(devices.filter(device => device.kind === 'audioinput'));
            setAudioOutputs(devices.filter(device => device.kind === 'audiooutput'));
        };
        getAudioDevices();
    }, []);

    useEffect(() => {
        const fetchMetadata = async () => {
            if (!meetingSessionRef.current || !channel.id) return;
            localStorage.setItem("voiceData", JSON.stringify(voiceData));

            const req = await fetch(`__SITE_BASE__/api/v1/teams/${channel.teamId}/channels/${channel.id}/metadata`, {
                method: "POST",
                headers: {
                    "content-type": "application/json",
                },
                body: JSON.stringify(voiceData),
            });

            const data = await req.json();
            if (!data.data) {
                await new Promise((resolve) => setTimeout(resolve, 2500));
                fetchMetadata();
            }
        };
        fetchMetadata();
    }, [voiceData, channel.teamId, channel.id]);

    useEffect(() => {
        const retryJoinVoiceChannel = async () => {
            if (channel.canRetry && channel.status === "disconnected") {
                setChannel((prevChannel: any) => ({
                    ...prevChannel,
                    status: "reconnecting",
                }));
                await new Promise((resolve) => setTimeout(resolve, channel.retryCount ? channel.retryCount * 5000 : 0));
                joinVoiceChannel(channel, channel.teamId, true);
            }
        };
        retryJoinVoiceChannel();
    }, [channel]);

    const joinVoiceChannel = async (channelc: Channel, teamId: string, isReconnect?: boolean) => {
        try {
            if (!teamId || !channelc.id) return;
            if (channel.id === channelc.id && !isReconnect) return navigate(`/teams/${teamId}/channels/${channelc.id}`);
            if (channelc.nsfw && localStorage.getItem("nsfwAcceptedChannels")!.split(",").indexOf(channelc.id) === -1) return navigate(`/teams/${teamId}/channels/${channelc.id}`);

            const devices = await navigator.mediaDevices.enumerateDevices();
            setAudioInputs(devices.filter(device => device.kind === 'audioinput'));
            setAudioOutputs(devices.filter(device => device.kind === 'audiooutput'));

            if (audioInputs.length === 0) {
                console.error("No audio input devices available");
                return;
            }

            const voiceSettings = localStorage.getItem("voiceSettings") ? JSON.parse(localStorage.getItem("voiceSettings") as string) : null;
            const selectedMic = voiceSettings?.mic?.deviceId || audioInputs[0].deviceId;
            const selectedSpeaker = voiceSettings?.speaker?.deviceId || "default";

            const c = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: selectedMic } });
            if (!c) return;

            setChannel({
                ...channelc,
                status: "connecting",
            });

            const getCredentials = await fetch(`__SITE_BASE__/api/v1/teams/${teamId}/channels/${channelc.id}/join?isMuted=${voiceData.isMuted}&isDeafened=${voiceData.isDeafened}`);
            const data = await getCredentials.json();

            const configuration = new Chime.MeetingSessionConfiguration(data.meetingData, data.attendeeData);
            const logger = new Chime.ConsoleLogger("SDK", Chime.LogLevel.OFF);

            setChannel({
                ...channelc,
                status: "rtcConnecting",
            });

            const deviceController = new Chime.DefaultDeviceController(logger);
            meetingSessionRef.current = new Chime.DefaultMeetingSession(configuration, logger, deviceController);

            await deviceController.startAudioInput(selectedMic);
            await deviceController.chooseAudioOutput(selectedSpeaker);
            meetingSessionRef.current.audioVideo.start();
        
            let audioElement = document.getElementById("voiceChannel") as HTMLAudioElement;
            if(!audioElement) {
                audioElement = document.createElement("audio");
                audioElement.id = "voiceChannel";
                audioElement.autoplay = true;
                document.head.appendChild(audioElement);
            }
            if (audioElement) meetingSessionRef.current.audioVideo.bindAudioElement(audioElement);

            setVoice({
                meetingSessionRef,
                audioElement,
            });

            setChannel({
                ...channelc,
                status: "connected",
            });

            setVoiceData(localStorage.getItem("voiceData") ? JSON.parse(localStorage.getItem("voiceData") as string) : { isMuted: false, isDeafened: false });
        } catch (error) {
            console.log(error);
            setChannel((prevChannel: any) => ({
                ...prevChannel,
                status: "disconnected",
                canRetry: true,
                retryCount: typeof prevChannel.retryCount === 'number' ? prevChannel.retryCount + 1 : 1,
            }));
        }
    };

    const leaveVoiceChannel = async () => {
        if (!meetingSessionRef.current) return;
        setChannel((prevChannel: any) => ({
            ...prevChannel,
            canRetry: false,
        }));
        meetingSessionRef.current.audioVideo.stop();
        meetingSessionRef.current.audioVideo.unbindAudioElement();
        meetingSessionRef.current.deviceController?.destroy();

        await fetch(`__SITE_BASE__/api/v1/teams/${channel.teamId}/channels/${channel.id}/leave`);

        setChannel({});
    };

    const setVoiceSettings = async (settings: any) => {
        if (meetingSessionRef.current && meetingSessionRef.current.audioVideo) {
            const { audioVideo } = meetingSessionRef.current;
            await audioVideo.startAudioInput(settings.mic.deviceId);
            await audioVideo.chooseAudioOutput(settings.speaker.deviceId);
        } else {
            console.error('AudioVideo object not initialized');
        }
    };
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const startScreenSharing = async (): Promise<MediaStream | undefined> => {
        if (!meetingSessionRef.current) return undefined;
        if (isScreenSharing) {
            stopScreenSharing();
            return 
        }
        const mediaStream = await navigator.mediaDevices.getDisplayMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true
            }
        });
        meetingSessionRef.current.audioVideo.startContentShare(mediaStream);
        setIsScreenSharing(true);
        return mediaStream;
    }

    const stopScreenSharing = async () => {
        if (!meetingSessionRef.current) return;
        meetingSessionRef.current.audioVideo.stopContentShare();
        setIsScreenSharing(false);
    }

    const [noiseSuppression, ] = useState(JSON.parse(localStorage.getItem("voiceSettings") as string)?.noiseSuppression || false);

    useEffect(() => {
        if (!meetingSessionRef.current) return;
        meetingSessionRef.current.audioVideo.realtimeSetVoiceFocus(
            noiseSuppression
        );
    }, [noiseSuppression]);

    const [ping, ] = useState<number>(0);

    return (
        <>
            <VoiceContext.Provider value={{ voice, setVoice, joinVoiceChannel, channel, setChannel, leaveVoiceChannel, voiceData, setVoiceData, meetingSessionRef, setVoiceSettings, startScreenSharing, isScreenSharing, ping }}>
                {children}
            </VoiceContext.Provider>
        </>
    );
};

export const useVoice = () => useContext(VoiceContext);